import "@tailwindplus/elements"
import {StreamActions, session, type SubmitterConfig} from "@hotwired/turbo";
import {Confirm} from "./utils/confirm.js";
import {Application} from "@hotwired/stimulus";
import {installFocusRestorePolicy} from "./focus-restore-policy.ts";
import {installRefusedSubmitReveal} from "./refused-submit-reveal.ts";
import {installFieldErrorClears} from "./field-error-clears.ts";
import {installTurboRenderTracker} from "./turbo-render-state.ts";
import {installFormSubmitRefreshHold, type RefreshingSession} from "./form-submit-refresh-hold.ts";
import {installBusySubmitButton} from "./busy-submit-button.ts";
import {installScrollAffordance} from "./scroll-affordance.ts";
import {installTurboFrameMissingHandler} from "./turbo-frame-missing.ts";
import {installLiveChannelReconnect} from "./turbo-live-channel-reconnect.ts";

declare module "@hotwired/turbo" {
    interface TurboGlobal {
        config: {
            forms: {
                confirm: (message: any, formElement: any, submitter: any) => Promise<unknown>;
                submitter: SubmitterConfig | "disabled" | "aria-disabled";
            };
        };
    }
}

declare global {
    let Stimulus: Application;
}

// Configure turbo confirm
Turbo.config.forms.confirm = Confirm.turboConfirmation

// Same-page focus + caret restore beside turbo-refresh-scroll=preserve (see focus-restore-policy.ts).
installFocusRestorePolicy();

// A refused full-page save scrolls its error into view (see refused-submit-reveal.ts).
installRefusedSubmitReveal();

installFieldErrorClears();

// Document-wide "render in progress" flag the form controller consults (see turbo-render-state.ts).
installTurboRenderTracker();

// A record-page refresh must not cancel a full-page form submit on its way out (see form-submit-refresh-hold.ts).
installFormSubmitRefreshHold(StreamActions, session as unknown as RefreshingSession);

// A pressed submit button stays a Busy button until the result is on the page (see busy-submit-button.ts).
installBusySubmitButton(Turbo.config.forms);

// A frame response without its frame opens as a full page, never "Content missing" (see turbo-frame-missing.ts).
installTurboFrameMissingHandler();

// A live channel that closes (a deploy) opens again and its page catches up (see turbo-live-channel-reconnect.ts).
installLiveChannelReconnect();

// Suite-wide edge fades on every scroller, over the browser's own tinted scrollbar (see scroll-affordance.ts).
installScrollAffordance();

import dateRangePickerController from "./controllers/date-range-picker-controller.ts";
Stimulus.register('date-range-picker', dateRangePickerController);

import granularityPickerController from "./controllers/granularity-picker-controller.ts";
Stimulus.register('granularity-picker', granularityPickerController);

import dynamicTableController from "./controllers/dynamicTableController.js";
Stimulus.register('dynamic-table', dynamicTableController);

import elementController from "./controllers/element-controller.js";
Stimulus.register('element', elementController);

import formController from "./controllers/form-controller.js";
Stimulus.register('form', formController);

import modalController from "./controllers/modal-controller.js";
Stimulus.register('modal', modalController);

import searchableSelectController from "./controllers/searchable-select-controller.js";
Stimulus.register('searchable-select', searchableSelectController);

import submitOnCloseController from "./controllers/submit-on-close-controller.ts";
Stimulus.register('submit-on-close', submitOnCloseController);

import tooltipController from "./controllers/tooltip-controller.js";
Stimulus.register('tooltip', tooltipController);

import codeBlockController from "./controllers/code-block-controller.js";
Stimulus.register('code-block', codeBlockController);

import chartController from "./controllers/chart-controller.js";
Stimulus.register('chart', chartController);

import richTextEditorController from "./controllers/rich-text-editor-controller.ts";
Stimulus.register('rich-text-editor', richTextEditorController);

import placeholderEditorController from "./controllers/placeholder-editor-controller.ts";
Stimulus.register('placeholder-editor', placeholderEditorController);

import formSelectController from "./controllers/form-select-controller.ts";
Stimulus.register('form-select', formSelectController);

import branchingSelectController from "./controllers/branching-select-controller.ts";
Stimulus.register('branching-select', branchingSelectController);

import endStateDateToggleController from "./controllers/end-state-date-toggle-controller.ts";
Stimulus.register('end-state-date-toggle', endStateDateToggleController);

import changelogBadgeController from "./controllers/changelog-badge-controller.ts";
Stimulus.register('changelog-badge', changelogBadgeController);

import changelogPageController from "./controllers/changelog-page-controller.ts";
Stimulus.register('changelog-page', changelogPageController);

import browserTimezoneController from "./controllers/browser-timezone-controller.ts";
Stimulus.register('browser-timezone', browserTimezoneController);

import postWhenShownController from "./controllers/post-when-shown-controller.ts";
Stimulus.register('post-when-shown', postWhenShownController);

import tickEnablesSubmitController from "./controllers/tick-enables-submit-controller.ts";
Stimulus.register('tick-enables-submit', tickEnablesSubmitController);
